import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { api, extractError } from "../lib/api";
import { billableBagsPreview, bagsSettlementHelp } from "../lib/bagsSettlement";
import { isTruckLoadContract, purchaseAllocKey } from "../lib/accountsSettlement";
import { contractSelectOptionLabel, contractSelectShortLabel } from "../lib/purchaseContractOptions";
import { useLookups } from "../context/LookupOptionsContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

function emptyLine() {
  return {
    purchase_contract_id: "",
    contract_number: "",
    quality: "",
    balance_qtl: null,
    net_weight_qtl: "",
    bags_unloaded: "",
    party_invoice_bags: "",
    weight_per_bag_qtl: "",
    bardana: "",
  };
}

function lineFromAlloc(alloc) {
  return {
    purchase_contract_id: String(alloc.purchase_contract_id),
    contract_number: alloc.contract_number,
    quality: alloc.variety,
    balance_qtl: null,
    net_weight_qtl: alloc.net_weight_qtl != null ? String(alloc.net_weight_qtl) : "",
    bags_unloaded:
      alloc.bags_unloaded != null
        ? String(alloc.bags_unloaded)
        : alloc.bags_settlement?.bags_unloaded != null
          ? String(alloc.bags_settlement.bags_unloaded)
          : "",
    party_invoice_bags:
      alloc.party_invoice_bags != null
        ? String(alloc.party_invoice_bags)
        : alloc.bags_settlement?.party_invoice_bags != null
          ? String(alloc.bags_settlement.party_invoice_bags)
          : "",
    weight_per_bag_qtl: alloc.weight_per_bag_qtl != null ? String(alloc.weight_per_bag_qtl) : "",
    bardana: alloc.bardana || alloc.contract_bardana || "",
  };
}

function contractLabel(c) {
  return contractSelectOptionLabel(c);
}

function contractForLine(ln, options) {
  return options.find((c) => String(c.id) === String(ln.purchase_contract_id));
}

/**
 * Accounts: add/edit purchase contracts on a truck and enter net qtl per contract.
 */
function normBag(value) {
  if (value === "" || value == null) return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

function serverUnloadedBags(alloc) {
  if (alloc?.bags_unloaded != null) return Number(alloc.bags_unloaded);
  if (alloc?.bags_settlement?.bags_unloaded != null) return Number(alloc.bags_settlement.bags_unloaded);
  return null;
}

function serverInvoiceBags(alloc) {
  if (alloc?.party_invoice_bags != null) return Number(alloc.party_invoice_bags);
  if (alloc?.bags_settlement?.party_invoice_bags != null) return Number(alloc.bags_settlement.party_invoice_bags);
  return null;
}

function linesMatchServer(lines, purchase, { truckLoadMode = false, splitTarget = null } = {}) {
  const allocs = purchase?.contract_allocations || [];
  if (!lines.length && !allocs.length) return true;
  if (lines.length !== allocs.length) return false;
  for (const ln of lines) {
    const alloc = allocs.find((a) => String(a.purchase_contract_id) === String(ln.purchase_contract_id));
    if (!alloc) return false;
    const localNetRaw = normBag(ln.net_weight_qtl);
    const localNet = localNetRaw != null
      ? localNetRaw
      : truckLoadMode && lines.length === 1 && splitTarget != null
        ? Number(splitTarget)
        : null;
    const serverNet = normBag(alloc.net_weight_qtl);
    if (localNet == null && serverNet == null) {
      // both empty
    } else if (localNet == null || serverNet == null || Math.abs(localNet - serverNet) > 0.001) {
      return false;
    }
    const localInvoice = normBag(ln.party_invoice_bags);
    const serverInvoice = serverInvoiceBags(alloc);
    if (localInvoice !== serverInvoice) return false;
    const localUnloaded = normBag(ln.bags_unloaded);
    const serverUnloaded = serverUnloadedBags(alloc);
    if (localUnloaded !== serverUnloaded) return false;
    if (!bardanaMatches(ln, alloc)) return false;
  }
  return true;
}

function bardanaMatches(ln, alloc) {
  const local = String(ln.bardana || "").trim().toUpperCase();
  const stored = String(alloc?.bardana || "").trim().toUpperCase();
  const contractDefault = String(alloc?.contract_bardana || "").trim().toUpperCase();
  if (stored) return local === stored;
  return !local || local === contractDefault;
}

export default forwardRef(function AccountsLinkedContractsEditor({ purchase, canEdit, onSaved, onDirtyChange }, ref) {
  const { optionsFor } = useLookups();
  const bardanaOptions = optionsFor("bardana");
  const allocs = purchase?.contract_allocations || [];
  const truckNet = purchase?.net_weight_qtl;
  const partyNet = purchase?.party_net_weight_qtl;
  const [pendingContracts, setPendingContracts] = useState([]);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const suppressDirtyRef = React.useRef(false);

  const loadPending = useCallback(async () => {
    if (!purchase?.party_id) return;
    setLoading(true);
    try {
      const { data } = await api.get("/purchase-contracts/pending-for-weighbridge", {
        params: { party_id: purchase.party_id, for_purchase_id: purchase.id },
      });
      setPendingContracts(data || []);
    } catch {
      setPendingContracts([]);
    } finally {
      setLoading(false);
    }
  }, [purchase?.party_id, purchase?.id]);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  const allocKey = useMemo(() => purchaseAllocKey(purchase), [purchase?.contract_allocations]);

  useEffect(() => {
    const fromAllocs = allocs.length ? allocs.map(lineFromAlloc) : [emptyLine()];
    setLines(fromAllocs);
  }, [purchase?.id, allocKey]);

  useEffect(() => {
    suppressDirtyRef.current = false;
  }, [purchase?.id]);

  const contractOptions = useMemo(() => {
    const byId = new Map();
    for (const c of pendingContracts) {
      byId.set(String(c.id), c);
    }
    for (const a of allocs) {
      const id = String(a.purchase_contract_id);
      if (!byId.has(id)) {
        byId.set(id, {
          id: a.purchase_contract_id,
          contract_number: a.contract_number,
          quality: a.variety,
          party_name: purchase?.party_name,
          price: a.rate_per_qtl,
          delivery_condition: a.delivery_condition,
          bardana: a.contract_bardana || a.bardana || "",
        });
      }
    }
    return [...byId.values()];
  }, [pendingContracts, allocs, purchase?.party_name]);

  const updateLine = (idx, patch) => {
    suppressDirtyRef.current = false;
    setLines((prev) => prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  };

  const onContractChange = (idx, contractId) => {
    suppressDirtyRef.current = false;
    const c = contractOptions.find((x) => String(x.id) === String(contractId));
    updateLine(idx, {
      purchase_contract_id: contractId,
      contract_number: c?.contract_number || "",
      quality: c?.quality || "",
      balance_qtl: c?.balance_qtl ?? null,
      bardana: c?.bardana || "",
    });
  };

  const addLine = () => {
    const used = new Set(lines.map((l) => String(l.purchase_contract_id)).filter(Boolean));
    const next = contractOptions.find((c) => !used.has(String(c.id)));
    if (!next) {
      toast.error("No more contracts available for this party");
      return;
    }
    suppressDirtyRef.current = false;
    setLines((prev) => [
      ...prev,
      {
        ...emptyLine(),
        purchase_contract_id: String(next.id),
        contract_number: next.contract_number,
        quality: next.quality,
        balance_qtl: next.balance_qtl ?? null,
        bardana: next.bardana || "",
      },
    ]);
  };

  const removeLine = (idx) => {
    if (lines.length <= 1) return;
    suppressDirtyRef.current = false;
    setLines((prev) => prev.filter((_, i) => i !== idx));
  };

  const truckLoadMode = useMemo(() => {
    const ids = new Set(lines.map((l) => String(l.purchase_contract_id)).filter(Boolean));
    return contractOptions.some((c) => ids.has(String(c.id)) && isTruckLoadContract(c));
  }, [lines, contractOptions]);

  const splitTarget = truckLoadMode && partyNet != null && Number(partyNet) > 0
    ? Number(partyNet)
    : truckNet != null
      ? Number(truckNet)
      : null;
  const splitLabel = truckLoadMode ? "party side weight" : "truck net";
  const weightFieldLabel = truckLoadMode ? "Party net qtl" : "Net qtl";
  const showNetWeightField = !(truckLoadMode && lines.length === 1);

  useEffect(() => {
    if (!truckLoadMode || lines.length !== 1 || splitTarget == null) return;
    setLines((prev) => {
      if (prev.length !== 1) return prev;
      const target = String(splitTarget);
      if (prev[0].net_weight_qtl === target) return prev;
      return [{ ...prev[0], net_weight_qtl: target }];
    });
  }, [truckLoadMode, lines.length, splitTarget]);

  const netSum = lines.reduce((s, l) => s + (Number(l.net_weight_qtl) || 0), 0);
  const allNetsFilled = lines.length > 0 && lines.every((l) => l.net_weight_qtl !== "" && Number(l.net_weight_qtl) > 0);
  const dirty = !linesMatchServer(lines, purchase, { truckLoadMode, splitTarget });

  useEffect(() => {
    // After a successful save, stay clean until the user edits again (avoids a
    // second Next click when refresh round-trip briefly looks "dirty").
    if (suppressDirtyRef.current) {
      onDirtyChange?.(false);
      return;
    }
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const save = async () => {
    if (!dirty) return true;
    const payload = [];
    for (let i = 0; i < lines.length; i += 1) {
      const ln = lines[i];
      if (!ln.purchase_contract_id) {
        toast.error(`Line ${i + 1}: select a contract`);
        return false;
      }
      const row = { purchase_contract_id: Number(ln.purchase_contract_id) };
      if (showNetWeightField && ln.net_weight_qtl !== "") {
        row.net_weight_qtl = Number(ln.net_weight_qtl);
      } else if (!showNetWeightField && splitTarget != null) {
        row.net_weight_qtl = Number(splitTarget);
      }
      if (ln.party_invoice_bags !== "") row.party_invoice_bags = Number(ln.party_invoice_bags);
      if (ln.bags_unloaded !== "") row.bags_unloaded = Number(ln.bags_unloaded);
      if (ln.weight_per_bag_qtl !== "") row.weight_per_bag_qtl = Number(ln.weight_per_bag_qtl);
      if (ln.bardana) row.bardana = ln.bardana;
      payload.push(row);
    }
    const ids = payload.map((r) => r.purchase_contract_id);
    if (new Set(ids).size !== ids.length) {
      toast.error("Each contract can only appear once");
      return false;
    }
    if (splitTarget != null) {
      const sum = payload.reduce((s, row) => s + (Number(row.net_weight_qtl) || 0), 0);
      const allNets = payload.every((row) => row.net_weight_qtl != null && Number(row.net_weight_qtl) > 0);
      if (payload.length > 1 && allNets && Math.abs(sum - splitTarget) > 0.05) {
        toast.error(
          `Contract weight total (${sum.toFixed(2)} qtl) must equal ${splitLabel} (${splitTarget.toFixed(2)} qtl)`,
        );
        return false;
      }
      if (payload.length === 1 && allNets && Math.abs(sum - splitTarget) > 0.05 && showNetWeightField) {
        toast.error(
          truckLoadMode
            ? `After removing other contracts, set party net qtl to party side weight (${splitTarget.toFixed(2)} qtl) before saving`
            : `After removing other contracts, set net qtl to truck net (${splitTarget.toFixed(2)} qtl) before saving`,
        );
        return false;
      }
    }
    try {
      setSaving(true);
      await api.put(`/purchases/${purchase.id}/linked-contracts`, { lines: payload });
      suppressDirtyRef.current = true;
      onDirtyChange?.(false);
      toast.success("Linked contracts saved");
      await onSaved?.();
      return true;
    } catch (err) {
      toast.error(extractError(err));
      return false;
    } finally {
      setSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({ save }), [save]);

  if (!purchase) return null;

  return (
    <div
      className="rounded-md border border-teal-200 bg-teal-50/40 space-y-3 p-4"
      data-testid="accounts-linked-contracts-editor"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-xs font-medium text-teal-900">Linked purchase contracts</p>
          <p className="text-[10px] text-muted-foreground mt-1 leading-relaxed">
            {truckLoadMode
              ? "Truck load contract: distribute party side weight across contracts. Choose the bag type for each line — bardana deduction uses that size only. Enter party invoice bags — payable bags = lower of invoice and unloaded."
              : "When the truck has one quality across several contracts, enter net qtl per contract. Choose the bag type for each line — bardana deduction uses that size only. Party invoice bags are capped by unloaded bags."}{" "}
            {splitTarget != null
              ? `${truckLoadMode ? "Party side weight" : "Truck net"} is ${splitTarget} qtl; splits must match when multiple contracts are linked.`
              : truckLoadMode
                ? "Party side weight from weighbridge is required for truck load splits."
                : "Splits must match truck net when multiple contracts are linked."}
          </p>
        </div>
        {canEdit && (
          <Button type="button" variant="outline" size="sm" onClick={addLine} disabled={loading}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Add contract
          </Button>
        )}
      </div>

      <div className="space-y-3">
        {lines.map((ln, idx) => (
          <div
            key={`${ln.purchase_contract_id}-${idx}`}
            className="rounded-md border bg-card/90 p-3 space-y-3 min-w-0"
          >
            <div className="flex items-start justify-between gap-2 min-w-0">
              <div className="flex-1 min-w-0">
                <Label className="text-[10px] text-muted-foreground">Contract</Label>
                {canEdit ? (
                  <Select
                    value={ln.purchase_contract_id || "_none"}
                    onValueChange={(v) => onContractChange(idx, v === "_none" ? "" : v)}
                  >
                    <SelectTrigger className="mt-0.5 h-9 w-full min-w-0 [&>span]:truncate" data-testid={`accounts-contract-${idx}`}>
                      <SelectValue placeholder="Select contract…">
                        {contractSelectShortLabel(contractForLine(ln, contractOptions))}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="max-w-[min(100vw-2rem,28rem)]">
                      <SelectItem value="_none" disabled>Select contract…</SelectItem>
                      {contractOptions.map((c) => (
                        <SelectItem
                          key={c.id}
                          value={String(c.id)}
                          disabled={lines.some(
                            (l, i) => i !== idx && String(l.purchase_contract_id) === String(c.id),
                          )}
                        >
                          {contractLabel(c)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-xs font-mono mt-1 break-all">{ln.contract_number || ln.purchase_contract_id}</p>
                )}
                {ln.balance_qtl != null && (
                  <p className="text-[10px] text-muted-foreground mt-1">Balance: {ln.balance_qtl} qtl</p>
                )}
              </div>
              {canEdit && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0 text-muted-foreground hover:text-red-600"
                  onClick={() => removeLine(idx)}
                  disabled={lines.length <= 1}
                  aria-label="Remove contract"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>

            <div className={`grid gap-3 ${showNetWeightField ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-2 sm:grid-cols-3"}`}>
              <div className="min-w-0 col-span-2">
                <Label className="text-[10px] text-muted-foreground">Bag type</Label>
                <Select
                  value={ln.bardana || "_none"}
                  onValueChange={(v) => updateLine(idx, { bardana: v === "_none" ? "" : v })}
                  disabled={!canEdit}
                >
                  <SelectTrigger className="mt-0.5 h-9" data-testid={`accounts-bardana-${idx}`}>
                    <SelectValue placeholder="Select bag type…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none" disabled>Select bag type…</SelectItem>
                    {(bardanaOptions.some((o) => o.value === ln.bardana) || !ln.bardana
                      ? bardanaOptions
                      : [...bardanaOptions, { value: ln.bardana, label: ln.bardana }]
                    ).map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Bardana deduction for this contract uses this bag size only.
                </p>
              </div>
              {showNetWeightField ? (
                <div className="min-w-0">
                  <Label className="text-[10px] text-muted-foreground">{weightFieldLabel}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    className="h-9 mt-0.5"
                    value={ln.net_weight_qtl}
                    onChange={(e) => updateLine(idx, { net_weight_qtl: e.target.value })}
                    disabled={!canEdit}
                    data-testid={`accounts-net-${idx}`}
                  />
                </div>
              ) : (
                <div className="min-w-0 col-span-2 sm:col-span-4">
                  <p className="text-[10px] text-teal-900/80">
                    Party side weight ({splitTarget != null ? `${splitTarget} qtl` : "from step 1"}) applies to this contract automatically.
                  </p>
                </div>
              )}
              <div className="min-w-0">
                <Label className="text-[10px] text-muted-foreground">Unloaded bags</Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  className="h-9 mt-0.5"
                  value={ln.bags_unloaded}
                  onChange={(e) => updateLine(idx, { bags_unloaded: e.target.value })}
                  disabled={!canEdit}
                  data-testid={`accounts-unloaded-bags-${idx}`}
                />
              </div>
              <div className="min-w-0">
                <Label className="text-[10px] text-muted-foreground">Party invoice bags</Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  className="h-9 mt-0.5"
                  value={ln.party_invoice_bags}
                  onChange={(e) => updateLine(idx, { party_invoice_bags: e.target.value })}
                  disabled={!canEdit}
                  data-testid={`accounts-invoice-bags-${idx}`}
                />
              </div>
              <div className="min-w-0">
                <Label className="text-[10px] text-muted-foreground">Payable bags</Label>
                <Input
                  type="text"
                  className="h-9 mt-0.5 bg-teal-50/80 font-medium tabular-nums"
                  value={billableBagsPreview(ln.bags_unloaded, ln.party_invoice_bags) ?? "—"}
                  readOnly
                  disabled
                  tabIndex={-1}
                />
              </div>
              <div className="min-w-0 col-span-2 sm:col-span-4">
                <p className="text-[10px] text-teal-900/80">
                  {bagsSettlementHelp(
                    ln.bags_unloaded,
                    ln.party_invoice_bags,
                    billableBagsPreview(ln.bags_unloaded, ln.party_invoice_bags),
                  )}
                </p>
              </div>
              <div className="min-w-0 col-span-2 sm:col-span-1">
                <Label className="text-[10px] text-muted-foreground">qtl/bag</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.0001"
                  className="h-9 mt-0.5"
                  placeholder="Optional"
                  value={ln.weight_per_bag_qtl}
                  onChange={(e) => updateLine(idx, { weight_per_bag_qtl: e.target.value })}
                  disabled={!canEdit}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {splitTarget != null && allNetsFilled && showNetWeightField && (
        <p
          className={`text-[10px] tabular ${
            Math.abs(netSum - splitTarget) > 0.05 ? "text-amber-800" : "text-teal-800"
          }`}
        >
          Contract total: {netSum.toFixed(2)} qtl · {splitLabel}: {splitTarget.toFixed(2)} qtl
          {Math.abs(netSum - splitTarget) > 0.05
            && (lines.length > 1
              ? " — totals must match before save"
              : truckLoadMode
                ? " — update party net qtl to party side weight after removing other contracts"
                : " — update net qtl to truck net after removing other contracts")}
        </p>
      )}

    </div>
  );
});
